import type { CountryCode, E164Number, NumberType } from 'libphonenumber-js';
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js/max';

// Localised re-exported type defn so consumers don't have to know to import from
// libphonenumber-js directly for this type
export type PhoneCountryCode = CountryCode;

type FullPhoneParser = {
	type: NumberType;
	country: CountryCode | undefined;
	international: string;
	national: string;
	number: E164Number;
};

type FullPhoneParserValid = FullPhoneParser & { isValid: true };
type FullPhoneParserInvalid = { isValid: false };

export enum PhoneFormattingOption {
	National = 'national',
	International = 'international',
	Unformatted = 'unformatted'
}

/**
 * Parses a phone number string for a given country code.
 *
 * Checks if the input string is a possible phone number for the specified country.
 * If valid, returns the parsed phone number object; otherwise, returns `null`.
 *
 * @param input - The phone number string to parse.
 * @param countryCode - The country code to use for parsing.
 * @returns The parsed phone number object if valid, otherwise `null`.
 */
export const parsePhone = (input: string, countryCode: PhoneCountryCode) => {
	const canParse = isPossiblePhoneNumber(input, countryCode);
	if (input && canParse) {
		return parsePhoneNumber(input, countryCode);
	}
	return null;
};

/**
 * Attempts to format mobile numbers. Can pass in a number or string, but will ALWAYS return a string
 *
 * @param input - The input value to try format, takes a number or string
 * @param countryCode - Country code to use for formatting
 * @param formattingOption - Format as national (04xx xxx xxx) or international number (+61 4xx xxx xxx) or unformatted (+614xxxxxxxx). If not provided this function will return a 'national' formatted number if the number's country is the same as the countryCode, else an 'international' format will be returned
 *
 * @returns Nicely formatted phone/mobile as a string or the original input that has been converted to string
 */
export function formatPhoneNumber(
	input: string | number | undefined,
	countryCode: PhoneCountryCode,
	formattingOption?: PhoneFormattingOption
): string {
	if (input === undefined || input === null) {
		return '';
	}

	const strInput = typeof input === 'string' ? input : input.toString();

	try {
		const phone = parsePhone(strInput, countryCode);
		if (phone) {
			const defaultFormatting = phone.country === countryCode ? 'national' : 'international';
			const formatting = formattingOption ?? defaultFormatting;

			switch (formatting) {
				case 'national':
					return phone.formatNational();
				case 'international':
					return phone.formatInternational();
				case 'unformatted':
					return phone.number;
				default:
					return phone.formatNational();
			}
		}
		return strInput;
	} catch (e) {
		console.error(`Error parsing number: ${e}`);
	}
	return strInput;
}

/**
 * Parses a phone number input and returns detailed information about the number.
 *
 * @param input - The phone number to parse, as a string or number.
 * @param countryCode - The country code to use for parsing the phone number.
 * @returns An object containing parsed phone number details if valid, or an invalid result if parsing fails.
 *
 * @remarks
 * - Uses `parsePhone` to process the input.
 * - Returns an object with phone type, country, international and national formats, number, and validity status if parsing is successful.
 * - Returns `{ isValid: false }` if parsing fails or the input is invalid.
 */
export const fullPhoneParser = (input: string | number, countryCode: PhoneCountryCode): FullPhoneParserValid | FullPhoneParserInvalid => {
	const strInput = typeof input === 'string' ? input : input.toString();
	try {
		const phone = parsePhone(strInput, countryCode);
		if (phone) {
			return {
				type: phone.getType(),
				country: phone.country,
				international: phone.formatInternational(),
				national: phone.formatNational(),
				number: phone.number,
				isValid: phone.isPossible()
			};
		}
	} catch (e) {
		console.error(`Error parsing number: ${e}`);
	}
	return { isValid: false };
};

/**
 * Checks if a given phone number is valid for any of the specified allowed regions.
 *
 * This function attempts to parse and validate the provided phone number against each region
 * in the `allowedRegions` array. It returns `true` if the phone number is valid for at least
 * one of the allowed regions, and `false` otherwise.
 *
 * @param phoneNumber - The phone number to validate, as a string.
 * @param allowedRegions - An array of country codes (`CountryCode`) representing the regions to check against.
 * @returns `true` if the phone number is valid for any of the allowed regions, otherwise `false`.
 */
export const isValidNumberForAllowedRegions = (phoneNumber: string, allowedRegions: Array<PhoneCountryCode>) => {
	const regions = allowedRegions;

	try {
		const res = regions.some(r => {
			const parsedPhoneNumber = parsePhoneNumber(phoneNumber, r);
			if (!parsedPhoneNumber?.country || !regions.includes(parsedPhoneNumber.country)) return false;
			const valid = isValidPhoneNumber(phoneNumber, parsedPhoneNumber.country);
			return valid;
		});
		return res;
	} catch (e) {
		console.log(e, 'ERROR IS HERE');
		return false;
	}
};

/**
 * Checks if the provided phone number is valid for the specified country code.
 *
 * @param phoneNumber - The phone number to validate.
 * @param countryCode - The country code to validate the phone number against.
 * @returns `true` if the phone number is valid for the given country code, otherwise `false`.
 */
export const checkValidPhoneNumber = (phoneNumber: string, countryCode: PhoneCountryCode): boolean => {
	return isValidPhoneNumber(phoneNumber, countryCode);
};
